import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { verifyEmail } from "../../service/auth/email-verification.service";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState("");
    const hasVerified = useRef(false);

    useEffect(() => {
        const token = searchParams.get('token');

        if (!token) {
            setStatus('error');
            setMessage("Token de vérification manquant. Veuillez vérifier le lien dans votre email.");
            return;
        }

        // Éviter les appels multiples (React Strict Mode en développement)
        if (hasVerified.current) {
            return;
        }

        const verify = async () => {
            hasVerified.current = true;
            
            try {
                const data = await verifyEmail(token);
                setStatus('success');
                setMessage(data.message || "Votre email a été vérifié avec succès !");
                
                // Rediriger vers la page de connexion après 3 secondes
                setTimeout(() => {
                    navigate("/login");
                }, 3000);
            } catch (err: any) {
                setStatus('error');
                setMessage(err.response?.data?.message || "Erreur lors de la vérification de l'email");
            }
        };

        verify();
    }, [searchParams, navigate]);

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow-lg rounded-lg sm:px-10">
                    {status === 'loading' && (
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
                            <h2 className="mt-6 text-xl font-semibold text-gray-900">
                                Vérification en cours...
                            </h2>
                            <p className="mt-2 text-sm text-gray-600">
                                Veuillez patienter pendant que nous vérifions votre email.
                            </p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="text-center">
                            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto" />
                            <h2 className="mt-6 text-xl font-semibold text-gray-900">
                                Email vérifié avec succès !
                            </h2>
                            <p className="mt-2 text-sm text-gray-600">
                                {message}
                            </p>
                            <p className="mt-4 text-sm text-gray-500">
                                Redirection automatique vers la page de connexion dans quelques secondes...
                            </p>
                            <div className="mt-6">
                                <Link
                                    to="/login"
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    Se connecter maintenant
                                </Link>
                            </div>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="text-center">
                            <XCircleIcon className="h-16 w-16 text-red-500 mx-auto" />
                            <h2 className="mt-6 text-xl font-semibold text-gray-900">
                                Erreur de vérification
                            </h2>
                            <p className="mt-2 text-sm text-gray-600">
                                {message}
                            </p>
                            <div className="mt-6 space-y-3">
                                <Link
                                    to="/resend-verification"
                                    className="block w-full text-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    Renvoyer l'email de vérification
                                </Link>
                                <Link
                                    to="/login"
                                    className="block w-full text-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    Retour à la connexion
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerifyEmail;
